import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, XIcon } from 'lucide-react';
import { FadeInSection } from './ui/FadeInSection';
const displayedSpecialties = ['Primary Care', 'Dental', 'Behavioral Health', 'Physical Therapy', 'Dermatology', 'Orthopedics', 'Cardiology', 'Pediatrics', 'OB/GYN', 'Urgent Care', 'Chiropractic', 'Oncology', 'Vision', 'Podiatry', 'Speech Therapy', 'Occupational Therapy'];
const allSpecialties = [
// Common clinic specialties / front-desk search terms
'Primary Care', 'Dental', 'Behavioral Health', 'Mental Health', 'Physical Therapy', 'Occupational Therapy', 'Speech Therapy', 'Dermatology', 'Orthopedics', 'Cardiology', 'Cardiac', 'Pediatrics', 'Pediatric', 'OB/GYN', 'Obstetrical/Gynecological', 'Obstetrical', 'Gynecological', 'Urgent Care', 'Chiropractic', 'Oncology', 'Vision', 'Vision (Optometry)', 'Eye', 'Podiatry', 'Oral Surgery', 'Orthodontics', 'Periodontics', 'Prosthodontics', 'Endodontics', 'Audiology Exam', 'Neurology', 'Pulmonary', 'Renal', 'Endocrine', 'Gastrointestinal', 'Pathology', 'Anesthesia', 'Anesthesiologist', 'Cancer', 'Burn Care', 'Massage Therapy', 'Psychiatric', 'Psychotherapy', 'Cognitive Therapy', 'Substance Abuse', 'Alcoholism', 'Drug Addiction', 'Smoking Cessation', 'Social Work',
// Preventive / office / diagnostic services
'Consultation', 'Medical Care', 'Other Medical', 'Routine Exam', 'Routine Physical', 'Well Baby Care', 'Newborn Care', 'Screening Laboratory', 'Screening X-ray', 'Diagnostic Lab', 'Diagnostic Medical', 'Diagnostic X-Ray', 'Diagnostic Dental', 'MRI/CAT Scan', 'Pre-Admission Testing', 'Flu Vaccination', 'Pneumonia Vaccine', 'Immunizations', 'Mammogram High Risk Patient', 'Mammogram Low Risk Patient', 'Independent Medical Evaluation', 'Second Surgical Opinion', 'Third Surgical Opinion',
// Facility / visit / level of care
'Emergency Services', 'Hospital', 'Hospital - Inpatient', 'Hospital - Outpatient', 'Hospital - Emergency Accident', 'Hospital - Emergency Medical', 'Hospital - Room and Board', 'Hospital - Ambulatory Surgical', 'Ambulatory Service Center Facility', 'Professional (Physician)', 'Physician Visit - Office: Sick', 'Physician Visit - Office: Well', 'Professional (Physician) Visit - Office', 'Professional (Physician) Visit - Outpatient', 'Professional (Physician) Visit - Inpatient', 'Professional (Physician) Visit - Home', 'Professional (Physician) Visit - Nursing Home', 'Professional (Physician) Visit - Skilled Nursing Facility', 'Intensive Care', 'Coronary Care', 'Neonatal Intensive Care', 'Nursery', 'Transitional Nursery Care', 'Day Care (Psychiatric)', 'Partial Hospitalization (Psychiatric)', 'Residential Psychiatric Treatment', 'Mental Health Facility - Inpatient', 'Mental Health Facility - Outpatient', 'Mental Health Provider - Inpatient', 'Mental Health Provider - Outpatient', 'Psychiatric - Inpatient', 'Psychiatric - Outpatient', 'Psychiatric - Room and Board', 'Substance Abuse Facility - Inpatient', 'Substance Abuse Facility - Outpatient',
// Rehab / nursing / home health
'Physical Medicine', 'Rehabilitation', 'Rehabilitation - Inpatient', 'Rehabilitation - Outpatient', 'Rehabilitation - Room and Board', 'Cardiac Rehabilitation', 'Pulmonary Rehabilitation', 'Inhalation Therapy', 'Home Health Care', 'Home Health Visits', 'Home Health Prescriptions', 'Hospice', 'Skilled Nursing Care', 'Skilled Nursing Care - Room and Board', 'Private Duty Nursing', 'Private Duty Nursing - Home', 'Private Duty Nursing - Inpatient', 'Long Term Care', 'Transitional Care', 'Respite Care', 'Case Management',
// Women’s health / fertility
'Maternity', 'Family Planning', 'Infertility', 'In-vitro Fertilization', 'Abortion',
// Dental / vision details
'Dental Care', 'Dental Accident', 'Routine (Preventive) Dental', 'Adjunctive Dental Services', 'Dental Crowns', 'Maxillofacial Prosthetics', 'Eyewear and Eyewear Accessories', 'Frames', 'Lenses', 'Otological Exam',
// Surgery / treatment / advanced care
'Surgical', 'Surgical Assistance', 'Surgical Benefits - Facility', 'Surgical Benefits - Professional (Physician)', 'Invasive Procedures', 'Chemotherapy', 'Radiation Therapy', 'Transplants', 'Donor Procedures', 'Experimental Drug Therapy',
// Transport / equipment / supplies / pharmacy
'Licensed Ambulance', 'Cabulance', 'Air Transportation', 'Medically Related Transportation', 'Durable Medical Equipment', 'Durable Medical Equipment Purchase', 'Durable Medical Equipment Rental', 'Used Durable Medical Equipment', 'Prosthetic Device', 'Diabetic Supplies', 'Chronic Renal Disease (CRD) Equipment', 'Renal Supplies in the Home', 'Nonmedically Necessary Physical', 'Dialysis', 'Pharmacy', 'Free Standing Prescription Drug', 'Generic Prescription Drug', 'Generic Prescription Drug - Formulary', 'Generic Prescription Drug - Non-Formulary', 'Brand Name Prescription Drug', 'Brand Name Prescription Drug - Formulary', 'Brand Name Prescription Drug - Non-Formulary', 'Mail Order Prescription Drug', 'Mail Order Prescription Drug - Generic', 'Mail Order Prescription Drug - Brand Name',
// Misc / insurance terminology that may appear in benefit data
'General Benefits', 'Major Medical', 'Health Benefit Plan Coverage', 'Plan Waiting Period', 'Alternate Method', 'AIDS', 'Acupuncture', 'Chiropractic Office Visits', 'Podiatry - Office Visits', 'Podiatry - Nursing Home Visits', 'Lymphatic', 'Skin'];
const specialtyAliases = {
  // Plain-English -> canonical search terms
  'primary care': ['Primary Care', 'Medical Care', 'Professional (Physician) Visit - Office'],
  'pcp': ['Primary Care'],
  'family medicine': ['Primary Care'],
  'internal medicine': ['Primary Care'],
  'general practice': ['Primary Care'],
  'dental': ['Dental', 'Dental Care', 'Routine (Preventive) Dental', 'Diagnostic Dental'],
  'dentist': ['Dental', 'Dental Care'],
  'oral surgeon': ['Oral Surgery'],
  'orthodontist': ['Orthodontics'],
  'endodontist': ['Endodontics'],
  'periodontist': ['Periodontics'],
  'prosthodontist': ['Prosthodontics'],
  'behavioral health': ['Behavioral Health', 'Mental Health', 'Psychiatric', 'Psychotherapy', 'Cognitive Therapy'],
  'mental health': ['Mental Health', 'Behavioral Health', 'Psychiatric', 'Psychotherapy'],
  'therapy': ['Behavioral Health', 'Psychotherapy', 'Cognitive Therapy', 'Physical Therapy', 'Occupational Therapy', 'Speech Therapy'],
  'psychiatry': ['Psychiatric', 'Mental Health', 'Behavioral Health'],
  'psychology': ['Psychotherapy', 'Mental Health', 'Behavioral Health'],
  'substance abuse': ['Substance Abuse', 'Substance Abuse Facility - Inpatient', 'Substance Abuse Facility - Outpatient'],
  'addiction': ['Alcoholism', 'Drug Addiction', 'Substance Abuse'],
  'pt': ['Physical Therapy'],
  'physical therapy': ['Physical Therapy'],
  'ot': ['Occupational Therapy'],
  'occupational therapy': ['Occupational Therapy'],
  'speech': ['Speech Therapy'],
  'speech therapy': ['Speech Therapy'],
  'rehab': ['Rehabilitation', 'Rehabilitation - Outpatient', 'Rehabilitation - Inpatient', 'Physical Therapy'],
  'derm': ['Dermatology'],
  'dermatology': ['Dermatology'],
  'skin': ['Dermatology', 'Skin'],
  'ortho': ['Orthopedics'],
  'orthopedics': ['Orthopedics'],
  'orthopedic': ['Orthopedics'],
  'cardiology': ['Cardiology', 'Cardiac', 'Cardiac Rehabilitation'],
  'cardiologist': ['Cardiology', 'Cardiac'],
  'heart': ['Cardiology', 'Cardiac'],
  'pediatrics': ['Pediatrics', 'Pediatric', 'Well Baby Care', 'Newborn Care'],
  'pediatric': ['Pediatric', 'Pediatrics'],
  'pediatrician': ['Pediatrics', 'Pediatric'],
  'kids': ['Pediatrics', 'Pediatric'],
  'ob/gyn': ['OB/GYN', 'Obstetrical/Gynecological', 'Obstetrical', 'Gynecological'],
  'obgyn': ['OB/GYN', 'Obstetrical/Gynecological'],
  'gynecology': ['Gynecological', 'Obstetrical/Gynecological'],
  'obstetrics': ['Obstetrical', 'Obstetrical/Gynecological'],
  'womens health': ['OB/GYN', 'Obstetrical/Gynecological', 'Gynecological', 'Maternity', 'Family Planning'],
  'urgent care': ['Urgent Care', 'Emergency Services'],
  'walk in': ['Urgent Care'],
  'walk-in': ['Urgent Care'],
  'chiro': ['Chiropractic', 'Chiropractic Office Visits'],
  'chiropractic': ['Chiropractic', 'Chiropractic Office Visits'],
  'chiropractor': ['Chiropractic'],
  'oncology': ['Oncology', 'Cancer', 'Chemotherapy', 'Radiation Therapy'],
  'oncologist': ['Oncology', 'Cancer'],
  'cancer': ['Cancer', 'Oncology', 'Chemotherapy', 'Radiation Therapy'],
  'vision': ['Vision', 'Vision (Optometry)', 'Eye', 'Eyewear and Eyewear Accessories', 'Frames', 'Lenses'],
  'eye': ['Vision', 'Vision (Optometry)', 'Eye'],
  'eye doctor': ['Vision', 'Vision (Optometry)', 'Eye'],
  'optometry': ['Vision (Optometry)', 'Vision'],
  'glasses': ['Eyewear and Eyewear Accessories', 'Frames', 'Lenses'],
  'podiatry': ['Podiatry', 'Podiatry - Office Visits', 'Podiatry - Nursing Home Visits'],
  'podiatrist': ['Podiatry'],
  'foot': ['Podiatry'],
  'neurology': ['Neurology'],
  'pulmonary': ['Pulmonary', 'Pulmonary Rehabilitation'],
  'renal': ['Renal', 'Dialysis', 'Renal Supplies in the Home', 'Chronic Renal Disease (CRD) Equipment'],
  'dialysis': ['Dialysis', 'Renal'],
  'endocrinology': ['Endocrine'],
  'gi': ['Gastrointestinal'],
  'gastro': ['Gastrointestinal'],
  'audiology': ['Audiology Exam'],
  'hearing': ['Audiology Exam', 'Otological Exam'],
  'anesthesia': ['Anesthesia', 'Anesthesiologist'],
  'pain management': ['Physical Medicine', 'Rehabilitation', 'Massage Therapy'],
  'acupuncture': ['Acupuncture'],
  // Insurance / pharmacy / benefits terms
  'pharmacy': ['Pharmacy', 'Generic Prescription Drug', 'Brand Name Prescription Drug'],
  'prescription': ['Pharmacy', 'Generic Prescription Drug', 'Generic Prescription Drug - Formulary', 'Generic Prescription Drug - Non-Formulary', 'Brand Name Prescription Drug', 'Brand Name Prescription Drug - Formulary', 'Brand Name Prescription Drug - Non-Formulary', 'Mail Order Prescription Drug', 'Mail Order Prescription Drug - Generic', 'Mail Order Prescription Drug - Brand Name', 'Free Standing Prescription Drug'],
  'dme': ['Durable Medical Equipment', 'Durable Medical Equipment Purchase', 'Durable Medical Equipment Rental', 'Used Durable Medical Equipment'],
  'durable medical equipment': ['Durable Medical Equipment', 'Durable Medical Equipment Purchase', 'Durable Medical Equipment Rental', 'Used Durable Medical Equipment']
};
export function WhoItsFor() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredSpecialties = allSpecialties.filter(specialty => specialty.toLowerCase().includes(searchQuery.toLowerCase()));
  return <section className="relative py-24 overflow-hidden" data-id="element-450">
      {/* Layered background for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-slate-100/80 to-slate-50" data-id="element-451" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-brand-teal/[0.04] rounded-full blur-[80px] pointer-events-none" data-id="element-452" />

      {/* Real clinic photo background */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-453">
        <img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1920&q=80" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-[0.06]" data-id="element-454" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-455">
        {/* Header */}
        <FadeInSection data-id="element-456">
          <div className="text-center mb-10" data-id="element-457">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-458">
              Built for Healthcare Practices Like Yours
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-459">
              From solo practitioners to multi-location groups — enSwift
              supports the unique workflows of every specialty.
            </p>
          </div>
        </FadeInSection>

        {/* 175+ stat — focal point */}
        <FadeInSection delay={100} data-id="element-460">
          <div className="flex justify-center mb-10" data-id="element-461">
            <div className="inline-flex items-center gap-4 bg-white rounded-2xl px-8 py-5 shadow-md border border-slate-200/80" data-id="element-462">
              <span className="text-5xl md:text-6xl font-bold text-brand-teal tracking-tight" data-id="element-463">
                175+
              </span>
              <div className="text-left border-l border-slate-200 pl-4" data-id="element-464">
                <p className="text-sm font-bold text-slate-900" data-id="element-465">
                  Specialties Supported
                </p>
                <p className="text-xs text-slate-500" data-id="element-466">
                  Across medical, dental, behavioral & more
                </p>
              </div>
            </div>
          </div>
        </FadeInSection>

        {/* Specialty chips container */}
        <div className="flex flex-wrap justify-center gap-2.5 max-w-3xl mx-auto min-h-[120px]" data-id="element-467">
          <AnimatePresence mode="wait" data-id="element-468">
            {!isExpanded ?
          // Initial state: show curated specialties + expand button
          <motion.div key="collapsed" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="flex flex-wrap justify-center gap-2.5 w-full" data-id="element-469">
                {displayedSpecialties.map((specialty, index) => <motion.div key={specialty} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              delay: index * 0.04,
              duration: 0.35
            }} className="bg-white pl-1 pr-4 py-1.5 rounded-full border border-slate-200/80 shadow-sm flex items-center gap-2" data-id="element-470">
                    <div className="w-6 h-6 rounded-full bg-brand-teal/10 flex items-center justify-center" data-id="element-471">
                      <div className="w-2 h-2 rounded-full bg-brand-teal" data-id="element-472" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700 whitespace-nowrap" data-id="element-473">
                      {specialty}
                    </span>
                  </motion.div>)}
                <motion.button initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              delay: displayedSpecialties.length * 0.04,
              duration: 0.35
            }} onClick={() => setIsExpanded(true)} className="bg-brand-teal text-white pl-1 pr-5 py-1.5 rounded-full shadow-sm flex items-center gap-2 hover:bg-brand-dark transition-colors cursor-pointer" data-id="element-474">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center" data-id="element-475">
                    <span className="text-xs font-bold" data-id="element-476">+</span>
                  </div>
                  <span className="text-sm font-bold whitespace-nowrap" data-id="element-477">
                    163 more
                  </span>
                </motion.button>
              </motion.div> :
          // Expanded state: search bar + full list
          <motion.div key="expanded" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="w-full" data-id="element-478">
                {/* Search bar */}
                <motion.div initial={{
              scale: 0.9,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.3
            }} className="flex justify-center mb-6" data-id="element-479">
                  <div className="relative w-full max-w-md" data-id="element-480">
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-481" />
                    <input type="text" placeholder="Search specialties..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-12 py-3 rounded-full border-2 border-brand-teal/30 focus:border-brand-teal focus:outline-none text-sm font-medium shadow-sm" autoFocus data-id="element-482" />
                    <button onClick={() => {
                  setIsExpanded(false);
                  setSearchQuery('');
                }} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-id="element-483">
                      <XIcon className="w-4 h-4 text-slate-600" data-id="element-484" />
                    </button>
                  </div>
                </motion.div>

                {/* Full specialty grid */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto px-2 py-2" data-id="element-485">
                  {filteredSpecialties.map((specialty, index) => <motion.div key={specialty} initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: index * 0.01,
                duration: 0.2
              }} className="bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm text-sm font-medium text-slate-700 text-center hover:border-brand-teal/40 hover:bg-brand-light/20 transition-colors" data-id="element-486">
                      {specialty}
                    </motion.div>)}
                </motion.div>

                {filteredSpecialties.length === 0 && <p className="text-center text-slate-500 text-sm py-8" data-id="element-487">
                    No specialties found matching "{searchQuery}"
                  </p>}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}